import { create } from "zustand";

import { getPupilVideoData } from "@/context/PupilLessonAnalytics/pupilAnalyticsHelpers";
import type {
  AdditionalArgs,
  PupilLessonAnalyticsState,
} from "@/context/PupilLessonAnalytics/pupilLessonAnalytics.types";
import {
  trackLessonCompleted,
  trackLessonAbandoned,
  trackLessonAccessed,
  trackLessonStarted,
} from "@/context/PupilLessonAnalytics/trackingFunctions/lessonTracking";
import {
  trackIntroAbandoned,
  trackContentGuidanceAccepted,
  trackContentGuidanceDeclined,
  trackIntroCompleted,
  trackIntroStarted,
  trackWorksheetDownloaded,
} from "@/context/PupilLessonAnalytics/trackingFunctions/introTracking";
import {
  trackQuizAbandoned,
  trackQuizCompleted,
  trackQuizQuestionAttempt,
  trackQuizStarted,
} from "@/context/PupilLessonAnalytics/trackingFunctions/quizTracking";
import {
  trackVideoAbandoned,
  trackVideoCompleted,
  trackVideoStarted,
} from "@/context/PupilLessonAnalytics/trackingFunctions/videoTracking";
import {
  trackActivityResultsShared,
  trackLessonSummaryReviewed,
} from "@/context/PupilLessonAnalytics/trackingFunctions/reviewTracking";

export const usePupilLessonAnalyticsStore = create<PupilLessonAnalyticsState>()(
  (set, get) => ({
    track: null,
    additionalArgs: null,
    videoData: null,
    accessedLessonSlug: null,
    initialisePupilLessonAnalytics: ({
      track,
      pupilPathwayData,
      classroomAssignmentContext,
      lessonContent,
    }) => {
      const {
        courseId,
        itemId,
        attachmentId,
        clientEnvironment,
        classroomAssignmentId,
        submissionId,
        teacherLoginHint,
        pupilLoginHint,
      } = classroomAssignmentContext;
      const additionalArgs: AdditionalArgs = {
        ...pupilPathwayData,
        analyticsUseCase: "Pupil",
        clientEnvironment,
        classroomAssignmentId,
        courseId,
        itemId,
        attachmentId,
        submissionId,
        teacherLoginHint,
        pupilLoginHint,
      };

      if (get().accessedLessonSlug !== pupilPathwayData.lessonSlug) {
        trackLessonAccessed({ track, additionalArgs });
      }

      set({
        track,
        videoData: lessonContent ? getPupilVideoData(lessonContent) : null,
        additionalArgs,
        accessedLessonSlug: pupilPathwayData.lessonSlug,
      });
    },
    trackSectionStarted: (args) => {
      trackIntroStarted(get, args);
      trackQuizStarted(get, args);
      trackVideoStarted(get, args);
    },
    trackQuizQuestionAttempt: (args) => trackQuizQuestionAttempt(get, args),
    trackQuizCompleted: (args) => trackQuizCompleted(get, args),
    trackQuizAbandoned: ({ section, sectionResults, sectionStartedAt }) =>
      trackQuizAbandoned(get, section, sectionResults, sectionStartedAt),
    trackLessonStarted: () => trackLessonStarted(get),
    trackLessonCompleted: () => trackLessonCompleted(get),
    trackLessonAbandoned: () => trackLessonAbandoned(get),
    trackIntroCompleted: (args) => trackIntroCompleted(get, args),
    trackIntroAbandoned: ({ sectionStartedAt }) =>
      trackIntroAbandoned(get, sectionStartedAt),
    trackWorksheetDownloaded: () => trackWorksheetDownloaded(get),
    trackVideoCompleted: (args) => trackVideoCompleted(get, args),
    trackVideoAbandoned: ({ sectionResults, sectionStartedAt }) =>
      trackVideoAbandoned(get, sectionResults, sectionStartedAt),
    trackLessonSummaryReviewed: (args) => trackLessonSummaryReviewed(get, args),
    trackActivityResultsShared: (args) => trackActivityResultsShared(get, args),
    trackContentGuidanceAccepted: (args) =>
      trackContentGuidanceAccepted(get, args),
    trackContentGuidanceDeclined: (args) =>
      trackContentGuidanceDeclined(get, args),
  }),
);
